import { DeletedTourBag } from '@/api/models';
import { handAbbreviationToFullName } from '@/library/HandHelper';

export interface IColumnDef {
  header: string;
  // eslint-disable-next-line no-unused-vars
  getValue: (club: DeletedTourBag) => string | number | undefined;
}

// Common columns shared by all club types
const commonColumns: IColumnDef[] = [
  { header: 'User', getValue: (c) => c.appUserName },
  { header: 'Club Type', getValue: (c) => c.headTypeDescription },
  { header: 'Status', getValue: (c) => (c.isActive ? 'In the Bag' : 'Out of the Bag') },
  { header: 'Hand', getValue: (c) => handAbbreviationToFullName(c.hand) },
  { header: 'Model', getValue: (c) => c.headModelName },
  { header: 'Deleted Date', getValue: (c) => new Date(c.createDate).toLocaleString() },
];

// Reusable column fragments
const swingWtColumns: IColumnDef[] = [
  { header: 'Swing Wt Prefix', getValue: (c) => c.swingWtPrefixName },
  { header: 'Swing Wt Suffix', getValue: (c) => c.swingWtSuffixName },
];

const headWtColumn: IColumnDef = { header: 'Head Wt', getValue: (c) => c.headWeight };

const hotMeltColumns: IColumnDef[] = [
  { header: 'Hot Melt 1', getValue: (c) => c.ratGlue1 },
  { header: 'Hot Melt 2', getValue: (c) => c.ratGlue2 },
  { header: 'Hot Melt 3', getValue: (c) => c.ratGlue3 },
  { header: 'Hot Melt 4', getValue: (c) => c.ratGlue4 },
  { header: 'Hot Melt 5', getValue: (c) => c.ratGlue5 },
  { header: 'Hot Melt 6', getValue: (c) => c.ratGlue6 },
  { header: 'Hot Melt 7', getValue: (c) => c.ratGlue7 },
];

const shaftColumns: IColumnDef[] = [
  { header: 'Shaft', getValue: (c) => c.shaftName },
  { header: 'Shaft Cosmetic', getValue: (c) => c.shaftCosmetic },
  { header: 'Shaft Tipping Digit', getValue: (c) => c.shaftTippingDigit },
  { header: 'Shaft Tipping Fraction', getValue: (c) => c.shaftTippingFraction },
  { header: 'Measured', getValue: (c) => (c.isShaftLengthFinished ? 'Finished' : 'Cut') },
  { header: 'Shaft Length Digit', getValue: (c) => c.shaftLengthDigit },
  { header: 'Shaft Length Fraction', getValue: (c) => c.shaftLengthFraction },
];

const shaftColumnsNoTipping: IColumnDef[] = [
  { header: 'Shaft', getValue: (c) => c.shaftName },
  { header: 'Shaft Cosmetic', getValue: (c) => c.shaftCosmetic },
  { header: 'Measured', getValue: (c) => (c.isShaftLengthFinished ? 'Finished' : 'Cut') },
  { header: 'Shaft Length Digit', getValue: (c) => c.shaftLengthDigit },
  { header: 'Shaft Length Fraction', getValue: (c) => c.shaftLengthFraction },
];

const gripColumns: IColumnDef[] = [
  { header: 'Grip Type', getValue: (c) => c.gripType },
  { header: 'Grip Tape', getValue: (c) => c.tape },
  { header: 'Grip Size', getValue: (c) => c.gripSize },
  { header: 'Round / Rib', getValue: (c) => c.rndRib },
  { header: 'Logo', getValue: (c) => c.logo },
];

const notesColumn: IColumnDef = { header: 'Notes', getValue: (c) => c.notes };

// Metals shared columns (Driver/Fairway/Hybrid base)
const metalsBaseColumns: IColumnDef[] = [
  { header: 'Family', getValue: (c) => c.familyName },
  { header: 'Stated Loft', getValue: (c) => c.loftName },
  { header: 'Msrd Loft', getValue: (c) => c.measuredLoft },
  { header: 'Setting', getValue: (c) => c.settingName },
  { header: 'Sleeve', getValue: (c) => c.sleeveName },
  { header: 'CG / Flat Wt', getValue: (c) => c.cgFlatWeightName },
];

// Per-type column definitions

const driverColumns: IColumnDef[] = [
  ...commonColumns,
  ...metalsBaseColumns,
  { header: 'Fwd/Toe Wt Loc.', getValue: (c) => c.headWeightLocationLookupName },
  { header: 'Aft/Heel Wt Loc.', getValue: (c) => c.headWeightLocationAftLookupName },
  { header: 'CT', getValue: (c) => c.ct },
  { header: 'CT Location', getValue: (c) => c.ctLocation },
  ...swingWtColumns,
  headWtColumn,
  { header: 'Fwd/Toe Wt #', getValue: (c) => c.headWeightNumberLookupName },
  { header: 'Aft/Heel Wt #', getValue: (c) => c.headWeightNumberAftLookupName },
  { header: 'Laser No', getValue: (c) => c.laserNo },
  ...hotMeltColumns,
  ...shaftColumns,
  ...gripColumns,
  notesColumn,
];

const fairwayColumns: IColumnDef[] = [
  ...commonColumns,
  ...metalsBaseColumns,
  { header: 'Fwd/Toe Wt Loc.', getValue: (c) => c.headWeightLocationLookupName },
  { header: 'Aft/Heel Wt Loc.', getValue: (c) => c.headWeightLocationAftLookupName },
  ...swingWtColumns,
  headWtColumn,
  { header: 'Fwd/Toe Wt #', getValue: (c) => c.headWeightNumberLookupName },
  { header: 'Aft/Heel Wt #', getValue: (c) => c.headWeightNumberAftLookupName },
  { header: 'Laser No', getValue: (c) => c.laserNo },
  ...hotMeltColumns,
  ...shaftColumns,
  ...gripColumns,
  notesColumn,
];

const hybridColumns: IColumnDef[] = [
  ...commonColumns,
  ...metalsBaseColumns,
  { header: 'Fwd/Toe Wt Loc.', getValue: (c) => c.headWeightLocationLookupName },
  { header: 'Fwd/Toe Wt #', getValue: (c) => c.headWeightNumberLookupName },
  ...swingWtColumns,
  headWtColumn,
  { header: 'Laser No', getValue: (c) => c.laserNo },
  ...hotMeltColumns,
  ...shaftColumns,
  ...gripColumns,
  notesColumn,
];

const ironColumns: IColumnDef[] = [
  ...commonColumns,
  { header: 'Family', getValue: (c) => c.familyName },
  { header: 'Loft', getValue: (c) => `${c.loftDigit}${c.loftFraction}` },
  { header: 'Lie', getValue: (c) => `${c.lieDigit}${c.lieFraction}` },
  ...swingWtColumns,
  headWtColumn,
  { header: 'Back Wt', getValue: (c) => c.headWeightNumberBackLookupName },
  ...shaftColumnsNoTipping,
  ...gripColumns,
  notesColumn,
];

const wedgeColumns: IColumnDef[] = [
  ...commonColumns,
  { header: 'Family', getValue: (c) => c.familyName },
  { header: 'Family Note', getValue: (c) => c.familyNote },
  { header: 'Finish', getValue: (c) => c.headFinishName },
  { header: 'Finish Note', getValue: (c) => c.headFinishNote },
  { header: 'Loft', getValue: (c) => `${c.loftDigit}${c.loftFraction}` },
  { header: 'Lie', getValue: (c) => `${c.lieDigit}${c.lieFraction}` },
  { header: 'Stamping', getValue: (c) => c.stamping },
  { header: 'Grind Details', getValue: (c) => c.grindDetails },
  ...swingWtColumns,
  headWtColumn,
  ...shaftColumnsNoTipping,
  ...gripColumns,
  notesColumn,
];

const putterColumns: IColumnDef[] = [
  ...commonColumns,
  { header: 'Loft', getValue: (c) => `${c.loftDigit}${c.loftFraction}` },
  { header: 'Lie', getValue: (c) => `${c.lieDigit}${c.lieFraction}` },
  ...swingWtColumns,
  { header: 'Measured', getValue: (c) => (c.isShaftLengthFinished ? 'Finished' : 'Cut') },
  { header: 'Shaft Length Digit', getValue: (c) => c.shaftLengthDigit },
  { header: 'Shaft Length Fraction', getValue: (c) => c.shaftLengthFraction },
  { header: 'Grip Tape', getValue: (c) => c.tape },
  { header: 'Grip Type', getValue: (c) => c.gripType },
  { header: 'Finish', getValue: (c) => c.headFinishName },
  { header: 'Sightline', getValue: (c) => c.sightLine },
  { header: 'Neck', getValue: (c) => c.neck },
  { header: 'Stamping', getValue: (c) => c.stamping },
  notesColumn,
];

const utilityColumns: IColumnDef[] = [
  ...commonColumns,
  { header: 'Family', getValue: (c) => c.familyName },
  { header: 'Loft', getValue: (c) => `${c.loftDigit}${c.loftFraction}` },
  { header: 'Lie', getValue: (c) => `${c.lieDigit}${c.lieFraction}` },
  ...swingWtColumns,
  headWtColumn,
  { header: 'Back Wt', getValue: (c) => c.headWeightNumberBackLookupName },
  ...shaftColumns,
  ...gripColumns,
  notesColumn,
];

// Head type ID → columns map
const columnsMap: Record<number, IColumnDef[]> = {
  1: driverColumns,
  2: fairwayColumns,
  3: hybridColumns,
  4: ironColumns,
  5: wedgeColumns,
  6: putterColumns,
  7: utilityColumns,
};

export function getColumnsForHeadType(headTypeId: number): IColumnDef[] {
  return columnsMap[headTypeId] ?? commonColumns;
}
